import React, { useEffect, useRef, useState } from 'react';
import { Show, useNotify, usePermissions, useRecordContext, useRedirect, useRefresh, useTranslate } from 'react-admin';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { apiClient, type ApiError } from '../../utils/apiClient';
import { hasPermission } from '../../utils/hasPermission';
import { ReleaseBadges } from './KioskReleasesList';
import { RolloutActions } from './RolloutActions';
import { RolloutDialog } from './RolloutDialog';
import { errorLine, formatBytes, installStateLabel, rolloutPauseLine, rolloutTargetLabel, updateErrorLabel } from './labels';
import { saveBlob } from '../kiosk-release-keys/saveBlob';
import type { ReleaseDetail } from './types';

interface HandInstallFiles {
  manifestB64: string;
  signatureB64: string;
  fileName: string;
  downloadUrl: string;
  downloadUrlExpiresAt: string;
}

const base64Bytes = (text: string) => Uint8Array.from(atob(text), (char) => char.charCodeAt(0));

const BASE = '/api/backend/kiosk-releases';
const when = (iso: string | null) => (iso === null ? '-' : new Date(iso).toLocaleString());

const Row = ({ label, value, testId }: { label: string; value: React.ReactNode; testId?: string }) => (
  <Box data-testid={testId} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, borderBottom: '1px solid #F0F0F0' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" component="div" sx={{ textAlign: 'right', wordBreak: 'break-all' }}>{value}</Typography>
  </Box>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Card sx={{ minWidth: 0 }}>
    <CardContent>
      <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
      {children}
    </CardContent>
  </Card>
);

const ReferenceEditor = ({ record, onSaved }: { record: ReleaseDetail; onSaved: () => void }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const savedOcl = record.oclReference === null ? '' : record.oclReference;
  const savedNotes = record.notes === null ? '' : record.notes;
  const [ocl, setOcl] = useState(savedOcl);
  const [notes, setNotes] = useState(savedNotes);
  const baseline = useRef({ ocl: savedOcl, notes: savedNotes });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const previous = baseline.current;
    setOcl((current) => (current === previous.ocl ? savedOcl : current));
    setNotes((current) => (current === previous.notes ? savedNotes : current));
    baseline.current = { ocl: savedOcl, notes: savedNotes };
  }, [savedOcl, savedNotes]);
  const oclChanged = ocl.trim() !== savedOcl;
  const notesChanged = notes.trim() !== savedNotes;
  const save = async () => {
    setBusy(true);
    try {
      const body: { oclReference?: string | null; notes?: string | null } = {};
      if (oclChanged) body.oclReference = ocl.trim() === '' ? null : ocl.trim();
      if (notesChanged) body.notes = notes.trim() === '' ? null : notes.trim();
      await apiClient.patch(`${BASE}/${record.id}`, body);
      notify(translate('resources.kiosk-releases.actions.done'), { type: 'success' });
      onSaved();
    } catch (error) {
      notify(errorLine(error as ApiError, translate), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };
  const tooShort = ocl.trim().length > 0 && ocl.trim().length < 3;
  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mt: 2 }}>
      <TextField label={translate('resources.kiosk-releases.fields.oclReference')} value={ocl} onChange={(event) => setOcl(event.target.value)} error={tooShort} inputProps={{ 'data-testid': 'ocl-reference', maxLength: 200 }} sx={{ flex: 1 }} />
      <TextField label={translate('resources.kiosk-releases.fields.notes')} value={notes} onChange={(event) => setNotes(event.target.value)} inputProps={{ 'data-testid': 'release-notes', maxLength: 2000 }} sx={{ flex: 2 }} />
      <Button variant="outlined" disabled={busy || tooShort || (!oclChanged && !notesChanged)} onClick={save} sx={{ textTransform: 'none' }}>{translate('resources.kiosk-releases.actions.save')}</Button>
    </Box>
  );
};

export const KioskReleaseDetailView = () => {
  const record = useRecordContext<ReleaseDetail>();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const { permissions } = usePermissions();
  const [rolloutOpen, setRolloutOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [packageLink, setPackageLink] = useState<{ fileName: string; url: string } | null>(null);
  if (!record) return null;
  const t = (key: string, options?: any) => translate(`resources.kiosk-releases.${key}`, options);
  const canWrite = hasPermission(permissions as string[] | null | undefined, 'kiosk-releases:write:all');
  const notBeforeOf = (rolloutId: string) => {
    const rollout = record.rollouts.find((candidate) => candidate.id === rolloutId);
    return rollout === undefined ? null : rollout.notBefore;
  };

  const closeRevoke = () => {
    setRevokeOpen(false);
    setRevokeReason('');
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await apiClient.post(`${BASE}/${record.id}/revoke`, { reason: revokeReason.trim() });
      closeRevoke();
      refresh();
    } catch (error) {
      notify(errorLine(error as ApiError, translate), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handInstall = async () => {
    setBusy(true);
    try {
      const answer = await apiClient.get<{ data: HandInstallFiles }>(`${BASE}/${record.id}/files`);
      saveBlob(new Blob([base64Bytes(answer.data.manifestB64)], { type: 'application/json' }), 'manifest.json');
      saveBlob(new Blob([answer.data.signatureB64], { type: 'text/plain' }), 'manifest.sig');
      setPackageLink({ fileName: answer.data.fileName, url: answer.data.downloadUrl });
    } catch (error) {
      notify(errorLine(error as ApiError, translate), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await apiClient.delete(`${BASE}/${record.id}`);
      redirect('list', 'kiosk-releases');
    } catch (error) {
      notify(errorLine(error as ApiError, translate), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5">{t('title', { version: record.version })}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
            <Chip size="small" label={t(`kinds.${record.kind}`)} />
            <Chip size="small" label={t(`statuses.${record.status}`)} color={record.status === 'ready' ? 'success' : 'default'} />
            <ReleaseBadges record={record} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {record.status === 'ready' && <Button variant="outlined" disabled={busy || !record.keyActive} onClick={handInstall} sx={{ textTransform: 'none' }}>{t('files')}</Button>}
          {record.status === 'ready' && packageLink !== null && (
            <Button variant="contained" color="success" component="a" href={packageLink.url} target="_blank" rel="noopener noreferrer" download={packageLink.fileName} sx={{ textTransform: 'none' }}>
              {t('filesPackage', { file: packageLink.fileName })}
            </Button>
          )}
          {canWrite && record.status === 'ready' && <Button variant="contained" disabled={!record.keyActive} onClick={() => setRolloutOpen(true)} sx={{ textTransform: 'none' }}>{t('rollout.new')}</Button>}
          {canWrite && record.status === 'ready' && <Button color="error" onClick={() => setRevokeOpen(true)} sx={{ textTransform: 'none' }}>{t('actions.revoke')}</Button>}
          {canWrite && record.status === 'uploading' && <Button color="error" disabled={busy} onClick={remove} sx={{ textTransform: 'none' }}>{t('actions.delete')}</Button>}
        </Box>
      </Box>

      {record.status === 'ready' && !record.keyActive && <Alert severity="warning">{t('keyRetired')}</Alert>}

      {record.uploadError !== null && <Alert severity="error">{`${t('fields.uploadError')}: ${record.uploadError}`}</Alert>}
      {record.revokeReason !== null && <Alert severity="info">{`${t('fields.revokeReason')}: ${record.revokeReason}`}</Alert>}

      <Section title={t('sections.details')}>
        <Row label={t('fields.build')} value={record.build === null ? '-' : String(record.build)} />
        <Row label={t('fields.gitCommit')} value={record.gitCommit === null ? '-' : record.gitCommit} />
        <Row label={t('fields.keyId')} value={record.keyId === null ? '-' : record.keyId} />
        <Row label={t('fields.environmentPolicy')} value={record.environmentPolicy === null ? '-' : record.environmentPolicy.join(', ')} />
        <Row label={t('fields.minLauncherVersion')} value={record.minLauncherVersion === null ? '-' : record.minLauncherVersion} />
        <Row label={t('fields.journalSchema')} value={record.journalSchema === null ? '-' : String(record.journalSchema)} />
        <Row label={t('fields.ocrlVersion')} value={record.ocrlVersion === null ? '-' : record.ocrlVersion} />
        <Row label={t('fields.sizeBytes')} value={formatBytes(record.sizeBytes)} />
        <Row testId="manifest-files" label={t('fields.manifestFiles')} value={record.manifestFiles === null ? '-' : String(record.manifestFiles)} />
        <Row label={t('fields.createdAt')} value={when(record.createdAt)} />
        {canWrite ? <ReferenceEditor record={record} onSaved={refresh} /> : (
          <>
            <Row label={t('fields.oclReference')} value={record.oclReference === null ? '-' : record.oclReference} />
            <Row label={t('fields.notes')} value={record.notes === null ? '-' : record.notes} />
          </>
        )}
      </Section>

      <Section title={t('sections.rollouts')}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('columns.target')}</TableCell>
                <TableCell>{t('columns.channel')}</TableCell>
                <TableCell>{t('columns.policy')}</TableCell>
                <TableCell>{t('columns.status')}</TableCell>
                <TableCell>{t('columns.progress')}</TableCell>
                {canWrite && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {record.rollouts.map((rollout) => (
                <TableRow key={rollout.id} data-testid={`rollout-${rollout.id}`}>
                  <TableCell>{rolloutTargetLabel(rollout, translate)}</TableCell>
                  <TableCell>{t(`rollout.channels.${rollout.channel}`)}</TableCell>
                  <TableCell>{rollout.policy === 'all_at_once' ? t('rollout.policies.all_at_once') : t('rollout.paceShort', { count: rollout.siteConcurrency })}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{t(`rolloutStatuses.${rollout.status}`)}</Typography>
                    {rolloutPauseLine(rollout, translate) !== null && <Typography variant="caption" color="error">{rolloutPauseLine(rollout, translate)}</Typography>}
                  </TableCell>
                  <TableCell>{t('progress', { installed: rollout.progress.installed, targeted: rollout.progress.targeted })}</TableCell>
                  {canWrite && <TableCell><RolloutActions rollout={rollout} onChanged={refresh} /></TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>

      <Section title={t('sections.installs')}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('columns.kiosk')}</TableCell>
                <TableCell>{t('columns.site')}</TableCell>
                <TableCell>{t('columns.state')}</TableCell>
                <TableCell>{t('columns.attempts')}</TableCell>
                <TableCell>{t('columns.lastError')}</TableCell>
                <TableCell>{t('columns.updatedAt')}</TableCell>
                <TableCell>{t('columns.lastHeartbeatAt')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {record.installs.map((install) => (
                <TableRow key={install.id} data-testid={`install-${install.id}`}>
                  <TableCell>{install.kioskName}</TableCell>
                  <TableCell>{install.siteName}</TableCell>
                  <TableCell>{installStateLabel(install, notBeforeOf(install.rolloutId), translate)}</TableCell>
                  <TableCell>{install.attempts}</TableCell>
                  <TableCell>{install.lastErrorCode === null ? '-' : updateErrorLabel(install.lastErrorCode, translate)}</TableCell>
                  <TableCell>{when(install.updatedAt)}</TableCell>
                  <TableCell>{when(install.lastHeartbeatAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>

      <RolloutDialog release={record} open={rolloutOpen} onClose={() => setRolloutOpen(false)} onCreated={refresh} />
      <Dialog open={revokeOpen} onClose={() => !busy && closeRevoke()} maxWidth="xs" fullWidth>
        <DialogTitle>{t('actions.revoke')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>{t('actions.revokeNote')}</Typography>
          <TextField autoFocus fullWidth multiline minRows={2} label={t('actions.revokeReason')} value={revokeReason} onChange={(event) => setRevokeReason(event.target.value)} inputProps={{ 'data-testid': 'revoke-reason', maxLength: 500 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRevoke} disabled={busy} sx={{ textTransform: 'none' }}>{translate('ra.action.cancel')}</Button>
          <Button color="error" variant="contained" disabled={busy || revokeReason.trim().length < 10} onClick={revoke} sx={{ textTransform: 'none' }}>{t('actions.revoke')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const KioskReleaseShow = () => (
  <Show title={false} actions={false} component="div">
    <KioskReleaseDetailView />
  </Show>
);
